import { Award, Users, TrendingUp, HeadphonesIcon, Target, Zap, CheckCircle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function WhyChooseUsPage() {
  const reasons = [
    {
      icon: <Award size={40} />,
      title: 'Industry Expertise',
      description: '10+ years of experience delivering excellence in IT training and technology solutions across diverse industries.',
    },
    {
      icon: <Users size={40} />,
      title: 'Expert Trainers',
      description: 'Learn from industry professionals with real-world experience in leading tech companies.',
    },
    {
      icon: <TrendingUp size={40} />,
      title: '95% Placement Rate',
      description: 'Outstanding track record of placing students in top companies with competitive packages.',
    },
    {
      icon: <HeadphonesIcon size={40} />,
      title: '24/7 Support',
      description: 'Dedicated support team available round-the-clock for all your queries and technical assistance.',
    },
    {
      icon: <Target size={40} />,
      title: 'Practical Approach',
      description: 'Hands-on training with real-world projects ensuring you are industry-ready from day one.',
    },
    {
      icon: <Zap size={40} />,
      title: 'Latest Technology',
      description: 'Training on cutting-edge tools and technologies that are in high demand in the job market.',
    },
  ];

  const benefits = [
    'Lifetime access to course materials',
    'Free access to updated content',
    'Resume building workshops',
    'Mock interview sessions',
    'Soft skills training',
    'LinkedIn profile optimization',
    'Job portal guidance',
    'Alumni network access',
    'Industry networking events',
    'Placement guarantee programs',
    'Flexible payment options',
    'Corporate training solutions',
  ];

  const differentiators = [
    {
      title: 'Comprehensive Curriculum',
      description: 'Our courses are designed in collaboration with industry experts to ensure relevance and completeness.',
    },
    {
      title: 'Individual Attention',
      description: 'Small batch sizes ensure personalized attention and better learning outcomes.',
    },
    {
      title: 'Real Projects',
      description: 'Work on actual industry projects to build a strong portfolio that impresses employers.',
    },
    {
      title: 'Certification',
      description: 'Industry-recognized certifications that add significant value to your resume.',
    },
  ];

  return (
    <div data-testid="why-choose-us-page" className="pt-20">
      <SEO 
        title="Why Choose Us - Best IT Training in Narasaraopeta"
        description="Discover why SKANDA IT SOLUTIONS is the best choice for IT training in Narasaraopeta. 10+ years experience, expert trainers, 95% placement rate, hands-on learning, modern infrastructure, and affordable fees."
        keywords="why choose SKANDA IT, best IT training, experienced trainers, high placement rate, affordable IT courses, modern lab facilities, career support, industry certifications"
        canonicalUrl="https://www.skandaitsolutions.com/why-choose-us"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 data-testid="why-hero-title" className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Why Choose SKANDA IT?
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Your success is our mission. Discover what makes us the preferred choice for IT training and solutions.
          </p>
        </div>
      </section>

      {/* Key Reasons */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="key-reasons-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            What Sets Us Apart
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Experience the difference with our proven track record and commitment to excellence
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <div key={index} data-testid={`reason-card-${index}`} className="service-card p-8 rounded-sm text-center">
              <div className="icon-wrapper mx-auto">
                {reason.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {reason.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 data-testid="stats-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Track Record
            </h2>
            <p className="text-xl text-blue-50">
              Numbers that speak for our excellence and dedication
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div data-testid="stat-students" className="text-center">
              <div className="text-6xl font-bold mb-2">500+</div>
              <div className="text-blue-100 text-lg">Students Trained</div>
            </div>
            <div data-testid="stat-placement" className="text-center">
              <div className="text-6xl font-bold mb-2">95%</div>
              <div className="text-blue-100 text-lg">Placement Success</div>
            </div>
            <div data-testid="stat-clients" className="text-center">
              <div className="text-6xl font-bold mb-2">200+</div>
              <div className="text-blue-100 text-lg">Corporate Clients</div>
            </div>
            <div data-testid="stat-experience" className="text-center">
              <div className="text-6xl font-bold mb-2">10+</div>
              <div className="text-blue-100 text-lg">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-container">
        <div className="text-center mb-16">
          <h2 data-testid="benefits-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Additional Benefits
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive support and resources to ensure your success
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div key={index} data-testid={`benefit-${index}`} className="flex items-center space-x-3 bg-white p-6 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
              <CheckCircle size={24} className="text-blue-600 flex-shrink-0" />
              <span className="text-slate-700 font-medium">{benefit}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 data-testid="differentiators-title" className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Key Differentiators
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differentiators.map((diff, index) => (
              <div key={index} data-testid={`differentiator-${index}`} className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <BookOpen size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {diff.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {diff.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 data-testid="cta-title" className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Join Thousands of Successful Students
          </h2>
          <p className="text-xl mb-10 text-blue-50 max-w-2xl mx-auto">
            Start your journey to a successful IT career with SKANDA IT SOLUTIONS today!
          </p>
          <Link to="/contact">
            <button data-testid="cta-button" className="bg-white text-blue-600 px-10 py-4 rounded-sm font-bold text-lg hover:bg-slate-100 transition-all duration-300 hover:shadow-xl">
              Enroll Now
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}